import AfSwitch from '@/components/AfSwitch';
import Constants from '@/Constants';
import { useProgramClass } from '@/hooks/useProgramClass';
import { ProgramType } from '@/models/Program';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import './style.scss';

interface OnChangeProps {
    hasProgramCertificate: boolean;
    hasCourseCertificate: boolean;
}

interface Props {
    enableCourseCertificate?: boolean;
    enableProgramCertificate?: boolean;
    handleOnChange: ({ hasProgramCertificate, hasCourseCertificate }: OnChangeProps) => void;
}

const CertificateSettingsSection: React.FC<Props> = ({ enableCourseCertificate, enableProgramCertificate, handleOnChange }) => {
    const programClassId: number = Number(useParams<{ classId: string }>().classId);

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const [enableProgramCertificateOption, setEnableProgramCertificateOption] = useState<boolean>(false);
    const [enableCourseCertificateOption, setEnableCourseCertificateOption] = useState<boolean>(false);

    const courseCertificateExists: boolean = useMemo(() => enableCourseCertificate !== undefined, [enableCourseCertificate]);
    const programCertificateExists: boolean = useMemo(() => enableProgramCertificate !== undefined, [enableProgramCertificate]);

    const { programClass } = useProgramClass({
        programClassId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const programType: ProgramType = programClass?.program.type || 'BUILDING';

    const isProgramDisabled: boolean = useMemo(() => programType === 'BUILDING', [programType]);

    const handleOnCourseChange = (checked: boolean): void => {
        setEnableCourseCertificateOption(checked);
    };

    const handleOnProgramChange = (checked: boolean): void => {
        if (isProgramDisabled) {
            setEnableProgramCertificateOption(false);
        } else {
            setEnableProgramCertificateOption(checked);
        }
    };

    const updateCourseCertificateOption = (): void => {
        if (courseCertificateExists) {
            setEnableCourseCertificateOption(!!enableCourseCertificate);
        }
    };

    const updateProgramCertificateOption = (): void => {
        if (programCertificateExists) {
            if (isProgramDisabled) {
                setEnableProgramCertificateOption(false);
            } else {
                setEnableProgramCertificateOption(!!enableProgramCertificate);
            }
        }
    };

    const onChange = () => {
        handleOnChange({ hasProgramCertificate: enableProgramCertificateOption, hasCourseCertificate: enableCourseCertificateOption });
    };

    useEffect(onChange, [enableCourseCertificateOption, enableProgramCertificateOption]);

    useEffect(updateCourseCertificateOption, [enableCourseCertificate, courseCertificateExists]);

    useEffect(updateProgramCertificateOption, [enableProgramCertificate, programCertificateExists]);

    const tooltip = useMemo(() => {
        const text = programCertificateExists ? strings.PROGRAM_CERTIFICATE_ALERT : strings.CERTIFICATE_ALERT;

        return (
            <Tooltip id="certificate-hint">
                <span dangerouslySetInnerHTML={{ __html: text }}></span>
            </Tooltip>
        );
    }, [strings.CERTIFICATE_ALERT, programCertificateExists]);

    return (
        <div className="certificate-settings-container">
            <strong>{strings.CERTIFICATE}</strong>
            <OverlayTrigger overlay={tooltip} placement="bottom">
                <i className="material-icons-outlined">{Constants.icons.ERROR_OUTLINE}</i>
            </OverlayTrigger>

            <div className="certificate-settings-container__options-section">
                {programCertificateExists && (
                    <div className="program-option">
                        <AfSwitch checked={enableProgramCertificateOption} disabled={isProgramDisabled} onChange={handleOnProgramChange} />
                        <span className={`m-left--xs align-text-bottom ${isProgramDisabled ? 'disabled' : ''}`}>{strings.PROGRAM}</span>
                    </div>
                )}

                {courseCertificateExists && (
                    <div className="course-option">
                        <AfSwitch checked={enableCourseCertificateOption} onChange={handleOnCourseChange} />
                        <span className="m-left--xs align-text-bottom">
                            {programCertificateExists ? strings.COURSE : strings.AVAILABLE_ON_FINISH}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(CertificateSettingsSection);
